import {
  type SwappedInboundMessage,
  getSwappedOrigin,
} from '@funkit/connect-core'
import { useTheme } from '@shopify/restyle'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  BackHandler,
  Linking,
  View,
  type ViewStyle,
} from 'react-native'
import Animated, { FadeOut } from 'react-native-reanimated'
import {
  WebView,
  type WebViewMessageEvent,
  type WebViewNavigation,
} from 'react-native-webview'

import { logger } from '../logging/logger'
import { useFunkitColorScheme } from '../theme/FunkitThemeProvider'
import type { FunkitTheme } from '../theme/toRestyleTheme'
import { buildSwappedThemeRN } from './buildSwappedTheme'
import { staysInWebView } from './paymentNavigation'
import {
  SWAPPED_WEBVIEW_INJECTED_JS,
  createWebViewSwappedTransport,
} from './webViewSwappedTransport'

export interface SwappedWebViewProps {
  /** The selected FOP's `embeddedFlowUrl` (a swapped.com/.dev widget URL). */
  url: string
  /** Typed, origin-validated inbound messages from the embed. */
  onMessage: (msg: SwappedInboundMessage) => void
  /**
   * Android hardware back once the WebView has no page history left to pop —
   * the same destination as the embed's own `SWAPPED_GO_BACK`.
   */
  onBack?: () => void
  style?: ViewStyle
}

/**
 * Renders the Swapped embedded payment flow in a `WebView` and bridges its
 * messages to the typed {@link SwappedTransport} contract — the React Native
 * counterpart of web's iframe. The {@link SWAPPED_WEBVIEW_INJECTED_JS} bridge
 * redirects the embed's `postMessage` to the native channel; `onMessage` is
 * parsed + origin-validated before reaching the handler.
 */
export function SwappedWebView({
  url,
  onMessage,
  onBack,
  style,
}: SwappedWebViewProps) {
  const webViewRef = useRef<WebView>(null)
  // Whether the WebView has its own history to pop — read by hardware back.
  const canGoBackRef = useRef(false)
  // Last main-frame URL reported by the WebView's NATIVE layer — the fallback
  // when `nativeEvent.url` is empty on a message event (some webview builds omit
  // it). Seeded with the widget url so a message before the first navigation
  // callback still validates against a real Swapped origin. Never sourced from
  // page JS, so it stays trustworthy for the fail-closed origin gate.
  const lastCommittedUrlRef = useRef(url)
  const { transport, dispatch } = useMemo(
    () =>
      createWebViewSwappedTransport({
        getWebView: () => webViewRef.current,
      }),
    [],
  )

  const theme = useTheme<FunkitTheme>()
  const { colorScheme } = useFunkitColorScheme()
  const swappedTheme = useMemo(
    () => buildSwappedThemeRN(theme, colorScheme === 'dark'),
    [theme, colorScheme],
  )
  // Latest theme/scheme without resubscribing the message handler below.
  const swappedThemeRef = useRef(swappedTheme)
  swappedThemeRef.current = swappedTheme
  const colorSchemeRef = useRef(colorScheme)
  colorSchemeRef.current = colorScheme

  // Themed cover until the embed paints. `startInLoadingState` can't do it: its
  // spinner clears on load end, exposing WKWebView's bare page canvas — dark
  // under a dark system appearance, the ENG-5506 flash.
  const [isEmbedReady, setIsEmbedReady] = useState(false)

  // A new url reloads the WebView, so cover it again. The timeout uncovers when
  // READY never lands, so a broken bridge can't hide the page for good.
  useEffect(() => {
    setIsEmbedReady(false)
    const timer = setTimeout(
      () => setIsEmbedReady(true),
      EMBED_READY_TIMEOUT_MS,
    )
    return () => clearTimeout(timer)
  }, [url])

  // Keep the latest handler without re-subscribing on every render. When the
  // embed announces SWAPPED_READY (its message listener is attached — reaches
  // us via the bridge's `window.opener` shim), send it the active theme:
  // the same READY-gated send web's iframe does via core `useSwappedSession`.
  const handlerRef = useRef(onMessage)
  handlerRef.current = onMessage
  useEffect(
    () =>
      transport.onMessage((msg) => {
        if (msg.type === 'SWAPPED_READY') {
          setIsEmbedReady(true)
          transport.send({
            type: 'SWAPPED_THEME',
            data: swappedThemeRef.current,
          })
          logger.info('connect-rn:swapped:theme_sent', {
            colorScheme: colorSchemeRef.current,
          })
        }
        handlerRef.current(msg)
      }),
    [transport],
  )

  // Android hardware back while this screen is showing (the embed draws its
  // own top bar, so there is no RN header to fall back on). Mounted only while
  // the flow is open on the Swapped step, so the subscription is scoped to it.
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack
  useEffect(() => {
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        if (canGoBackRef.current) {
          logger.info('connect-rn:swapped:hardware_back', { target: 'webview' })
          webViewRef.current?.goBack()
          return true
        }
        if (!onBackRef.current) {
          return false
        }
        logger.info('connect-rn:swapped:hardware_back', { target: 'flow' })
        onBackRef.current()
        return true
      },
    )
    return () => subscription.remove()
  }, [])

  const handleNavigationStateChange = (navState: WebViewNavigation) => {
    if (navState.url) {
      lastCommittedUrlRef.current = navState.url
    }
    canGoBackRef.current = navState.canGoBack === true
  }

  // A card payment necessarily leaves the embed origin — the PSP checkout, the
  // 3DS challenge and the post-login redirect to a per-partner Swapped host are
  // all different origins. `staysInWebView` is the allowlist that keeps those
  // in-app while genuinely external links still go to the system browser.
  const embedOrigin = getSwappedOrigin(url)

  // Request/event shapes inlined: the package index doesn't export them.
  const shouldStartLoad = (request: {
    url: string
    isTopFrame?: boolean
  }): boolean => {
    // Subframes stay on `originWhitelist` — the 3DS challenge iframe, whose ACS
    // host belongs to the cardholder's bank and can't be allowlisted. Android
    // hard-codes `isTopFrame: true` and never raises this for inner frames, so
    // a missing flag is a top-frame navigation there.
    if (request.isTopFrame === false || !request.url.startsWith('https:')) {
      return true
    }
    if (staysInWebView(request.url, embedOrigin)) {
      return true
    }
    openExternally(request.url, 'top_frame')
    return false
  }

  // `target="_blank"` / `window.open`. iOS cancels the navigation and routes it
  // here instead of to `shouldStartLoad`, so this is the only gate a PSP's 3DS
  // popup passes; Android (multiple windows off) collapses it into a top-frame
  // load that `shouldStartLoad` sees. There is no second window to draw into,
  // so an allowlisted target navigates this WebView and the rest go to the
  // system browser.
  const handleOpenWindow = (event: { nativeEvent: { targetUrl: string } }) => {
    const target = event.nativeEvent.targetUrl
    if (!staysInWebView(target, embedOrigin)) {
      openExternally(target, 'open_window')
      return
    }
    logger.info('connect-rn:swapped:open_window_inline', {
      host: hostOf(target),
    })
    webViewRef.current?.injectJavaScript(
      `window.location.href = ${toJsStringLiteral(target)};true;`,
    )
  }

  const handleMessage = (event: WebViewMessageEvent) => {
    // onNavigationStateChange doesn't fire for SPA route changes inside the
    // widget, but those never change the origin — all the gate reads.
    dispatch(
      event.nativeEvent.data,
      event.nativeEvent.url || lastCommittedUrlRef.current,
    )
  }

  // Load failures are recoverable (the embed shows its own retry UI) → WARN.
  // Event types inlined: the exact exported names vary across webview versions.
  const handleError = (event: {
    nativeEvent: { code: number; description: string; url: string }
  }) => {
    // Nothing is coming, so stop covering. HTTP errors still deliver a page
    // that can boot and send READY, so those wait for the timeout.
    setIsEmbedReady(true)
    logger.warn('connect-rn:swapped:webview:error', {
      code: String(event.nativeEvent.code),
      description: event.nativeEvent.description,
      url: event.nativeEvent.url,
    })
  }
  const handleHttpError = (event: {
    nativeEvent: { statusCode: number; url: string }
  }) => {
    logger.warn('connect-rn:swapped:webview:httpError', {
      status: String(event.nativeEvent.statusCode),
      url: event.nativeEvent.url,
    })
  }

  return (
    <View style={[fill, style]}>
      <WebView
        ref={webViewRef}
        source={{ uri: url }}
        onMessage={handleMessage}
        onNavigationStateChange={handleNavigationStateChange}
        onShouldStartLoadWithRequest={shouldStartLoad}
        onOpenWindow={handleOpenWindow}
        onError={handleError}
        onHttpError={handleHttpError}
        // iOS-only; Android's counterpart is `nestedScrollEnabled` below.
        scrollEnabled={false}
        // Keep the vertical drag inside the WebView. Without it Android lets the
        // parent chain intercept, so gorhom's sheet pan swallows the gesture and
        // the embed's own scrollers (its fiat currency list) never move.
        nestedScrollEnabled
        injectedJavaScriptBeforeContentLoaded={SWAPPED_WEBVIEW_INJECTED_JS}
        // The Swapped flow needs these to run its KYC / payment widgets.
        javaScriptEnabled
        domStorageEnabled
        // The embed relies on its own session cookies (login / KYC state).
        sharedCookiesEnabled
        thirdPartyCookiesEnabled
        // Cards / Apple Pay / Google Pay surfaces inside the embed.
        allowsInlineMediaPlayback
        // The embed auto-focuses its amount input on open. iOS suppresses the
        // keyboard for programmatic (non-tap) focus by default, so the input
        // would sit focused with no keyboard — raise it without a user gesture.
        // (iOS-only prop; Android already shows the keyboard on focus.)
        keyboardDisplayRequiresUserAction={false}
        // Drop iOS's form accessory bar (the ‹ › / Done toolbar above the
        // keyboard) — the embed has no multi-field navigation to drive it, so it
        // just adds a strip between the sheet and the keyboard. (iOS-only.)
        hideKeyboardAccessoryView
        // The embed renders its own bottom sheet anchored to the viewport bottom.
        // By default WKWebView ALSO insets its scroll view by the keyboard height,
        // so its content shifts up while the sheet itself (gorhom's `interactive`
        // keyboard behavior) is already moving up — double-compensating. Pin the
        // insets so the WebView stays a fixed canvas and the sheet is the single
        // thing that moves. (iOS-only.)
        automaticallyAdjustContentInsets={false}
        contentInsetAdjustmentBehavior="never"
        // Android: a new-window link loads in this WebView instead, where
        // `onShouldStartLoadWithRequest` decides its fate.
        setSupportMultipleWindows={false}
        // WKWebView routes SUBFRAME navigations through this gate too, and
        // react-native-webview cancels non-matching ones with no onError. The 3DS
        // card challenge is an `<iframe srcdoc>` (`about:srcdoc`) — without it the
        // card step renders blank. Safe to admit: it inherits the embed's origin
        // and the bridge already validates every message against the Swapped origin.
        originWhitelist={['https://*', 'about:srcdoc']}
        // Match the theme while the page itself paints (the platform WebView
        // is opaque white by default — visible as a flash in dark/custom themes).
        style={[fill, { backgroundColor: theme.background.modal }]}
      />
      {isEmbedReady ? null : (
        <Animated.View
          exiting={coverFadeOut}
          style={[loadingOverlay, { backgroundColor: theme.background.modal }]}
        >
          <ActivityIndicator color={theme.content.elements.secondary} />
        </Animated.View>
      )}
    </View>
  )
}

// Covers a slow embed without stranding the user behind a broken bridge.
const EMBED_READY_TIMEOUT_MS = 10_000

/** Hand a link the widget can't host to the system browser. Host only in logs. */
function openExternally(target: string, reason: 'top_frame' | 'open_window') {
  const host = hostOf(target)
  logger.info('connect-rn:swapped:external_link', { host, reason })
  Linking.openURL(target).catch((caught: unknown) => {
    logger.warn('connect-rn:swapped:external_link_failed', {
      host,
      reason: caught instanceof Error ? caught.name : 'unknown',
    })
  })
}

// JSON covers quotes and backslashes; the line separators are valid in JSON
// strings but terminate a JS one, so they need escaping on top.
function toJsStringLiteral(value: string): string {
  return JSON.stringify(value)
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}

function hostOf(target: string): string {
  try {
    return new URL(target).host
  } catch {
    return 'invalid'
  }
}

// Fast (250ms) motion token — what the Add money dropdown opens on.
const coverFadeOut = FadeOut.duration(250)

const fill: ViewStyle = { flex: 1 }

const loadingOverlay: ViewStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  alignItems: 'center',
  justifyContent: 'center',
}
